import { gql } from "@/gql/__generated__";
import { Get_Organization_AppsQuery } from "@/gql/__generated__/graphql";
import { ArrayElement } from "@/lib/ts-helpers/ArrayElement";
import { useMutation } from "@apollo/client";
import { GET_ORGANIZATION_APPS } from "../organization/getOrganizationApps";
import { useUserActiveOrganization } from "../user/useUserActiveOrganization";

const APP_DEPLOY_SHARE = gql(`
  mutation appDeployShare($deployID: ID!) {
    appDeployShare(deployID: $deployID) {
      __typename
      id
      name
      sharedURL
      current {
        __typename
        id
        status
        proxyURL
        createdAt
      }
    }
  }
`);

type CurrentDeployment = Extract<
  ArrayElement<
    Extract<
      Get_Organization_AppsQuery["organization"],
      { __typename: "Organization" }
    >["apps"]
  >["defaultDeployment"],
  { __typename: "AppDeployment" }
>["current"];

interface Props {
  deployID: string;
  appID: string;
}

export function useAppDeployShare({ deployID, appID }: Props) {
  const { activeOrganization } = useUserActiveOrganization();

  return useMutation(APP_DEPLOY_SHARE, {
    variables: { deployID },
    update: (cache, data) => {
      const deployment =
        data.data?.appDeployShare.__typename === "AppDeployment" &&
        data.data.appDeployShare;
      if (!deployment || !activeOrganization) return cache;

      cache.updateQuery(
        {
          query: GET_ORGANIZATION_APPS,
          variables: { organizationSlug: activeOrganization.slug }
        },
        (cachedOrganizationApps) => {
          if (
            !cachedOrganizationApps ||
            cachedOrganizationApps.organization.__typename !== "Organization"
          )
            return null;

          const updatedApps = cachedOrganizationApps.organization.apps.map(
            (app) =>
              app.id === appID
                ? {
                    ...app,
                    defaultDeployment: {
                      ...app.defaultDeployment,
                      ...deployment,
                      current: {
                        ...app.defaultDeployment?.current,
                        ...deployment.current
                      } as CurrentDeployment
                    }
                  }
                : app
          );

          return {
            ...cachedOrganizationApps,
            organization: {
              ...cachedOrganizationApps.organization,
              apps: updatedApps
            }
          };
        }
      );

      return cache;
    }
  });
}
